import { useMemo, useState } from 'react';
import {
  useGetProjectQuery,
  useGetReassignUsersQuery,
  usePatchProjectMutation,
} from 'app/services/projectBoardApi';
import { useBoardCurrentUser } from '../hooks/useProjectBoardData';
import { autofocusAttr } from 'hooks/useAutofocusAttr';

/**
 * ReassignPopup (project/view/reassign_popup.js → reassign_popup.tmpl),
 * inventory §3.8 / parity 46, 65–66:
 *
 * - Users from GET /api/v1.0/users/?reassign_to=true, excluding the current
 *   assignee (main.user_id).
 * - Assign stays `non-clickable` until a user is chosen; choosing one shows
 *   the contextual warning in #massage_warrning (3 variants per the live
 *   change_field logic); clearing the select clears the warning again.
 * - Assign: silent `PATCH {user_id}` then redirect rules — project-access
 *   current user on a by_project_user project → /dashboard/; network user
 *   assigning to a project-access user with no critical task started →
 *   /dashboard/; otherwise stays (cache refresh re-renders the form).
 * - Cancel carries autofocus="true" (ENTER closes); ESC handled globally.
 *
 * [NEG] honored: the dead `keyup .ahu-name` validation binding (parity 67).
 */
interface ReassignPopupProps {
  projectId: number;
  onClose: () => void;
}

const ReassignPopup = ({ projectId, onClose }: ReassignPopupProps) => {
  const { data: project } = useGetProjectQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const { data: users } = useGetReassignUsersQuery();
  const { data: currentUser } = useBoardCurrentUser();
  const [patchProject] = usePatchProjectMutation();
  const [selected, setSelected] = useState('');

  const candidates = useMemo(
    () => (users ?? []).filter((u) => u.id !== project?.user_id),
    [users, project?.user_id]
  );

  // change_field warning copy (reassign_popup.js — live source, 3 variants).
  const warning = useMemo(() => {
    if (!selected) return null;
    const selectedUser = (users ?? []).find((u) => u.id === Number(selected));
    if (currentUser?.access_type === 'project') {
      return "It will be removed from your Dashboard and will appear on the new user's Dashboard.";
    }
    if (selectedUser?.access_type === 'project') {
      return !project?.is_critical_task_started
        ? "It will be removed from your Dashboard and will appear on the new user's Dashboard."
        : "It will appear on the new user's Dashboard.";
    }
    return '';
  }, [selected, users, currentUser?.access_type, project?.is_critical_task_started]);

  const assign = async () => {
    if (!selected || !project) return;
    const value = parseInt(selected, 10);
    try {
      await patchProject({ id: projectId, body: { user_id: value } }).unwrap();
    } catch {
      return; // Backbone success-only callback: no redirect/close on error
    }
    const selectedUser = (users ?? []).find((u) => u.id === value);
    if (project.by_project_user) {
      // Moving project from project user — will lose viewing access anyway
      if (currentUser?.access_type === 'project') {
        window.location.replace('/dashboard/');
        return;
      }
    } else if (
      // Moving project without started critical task from network user to
      // project user — will lose viewing access
      selectedUser?.access_type === 'project' &&
      !project.is_critical_task_started
    ) {
      window.location.replace('/dashboard/');
      return;
    }
    onClose();
  };

  return (
    <div className="popup-wrapper create-popup active">
      <div className="popup-content col-xs-12">
        <div className="">
          <h2 className="popup-heading"> Reassign Project to another user?</h2>
        </div>
        <div className="col-xs-12">
          <div className="clearfix  popup-content-part col-xs-12">
            <div className="" id="massage_warrning">
              {warning !== null && <p>{warning}</p>}
            </div>

            <div className="col-xs-12 top">
              <div className="row">
                <div className=" col-xs-4">
                  <div className="row">
                    <span>Project </span>
                  </div>
                </div>
                <div className=" col-xs-8">
                  <div className="row">
                    <span> {project?.name ?? ''}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xs-12">
              <div className="row">
                <div className="col-xs-4">
                  <div className="row select-label">
                    <span> Reassign to </span>
                  </div>
                </div>
                <div className="col-xs-8">
                  <div className="row">
                    <select
                      className="long-input"
                      name="user_id"
                      id="users"
                      value={selected}
                      onChange={(e) => setSelected(e.target.value)}
                    >
                      <option value="">--</option>
                      {candidates.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.first_name} {u.middle_name} {u.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="popup-control-buttons col-xs-12">
            <div className="row">
              <div className="col-xs-7">
                <div className="row">
                  <p>You CANNOT undo this action!</p>
                </div>
              </div>
              <div className="col-xs-5">
                <div className="row">
                  <button
                    className={`add${selected ? '' : ' non-clickable'} not-highlighted-btn`}
                    name="assign"
                    onClick={assign}
                  >
                    Assign
                  </button>
                  <button
                    ref={autofocusAttr}
                    className="default-btn highlighted-btn"
                    name="cancel"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReassignPopup;
